import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { DataGrid } from '@mui/x-data-grid';
import { Box, CircularProgress, Divider, Grid2, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { RootState } from '@/src/datastore/store';
import {
  IApiClientResponse,
  IAttributeOptions,
  ILooseObject,
  INPUT_FIELD_TYPE_MAP,
  INPUT_TYPES_ENUM,
  ListingTableDataStateType,
  UI_TYPE_INPUT_TYPE_MAP
} from '@/src/types/common.interface';
import AppConfig from '@/src/config/AppConfig';
import AttributesConfig, { IAttributeDef } from '@/src/config/AttributesConfig';
import AllRolesService from '@/src/services/all-roles/allRolesService';
import CustomTablePaginationActions from '@/src/components/common/CustomTablePaginationActions';
import DynamicBreadCrumbs from '@/src/components/common/DynamicBreadCrumbs';
import NoAuthorization from '@/src/components/common/NoAuthorization';
import DynamicInput from '@/src/components/common/DynamicInput';
import lightTheme from '@/src/styles/themes/lightTheme';
import AppMessages from '@/src/config/message_config/AppMessages';

const RoleDetails = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState<ILooseObject>({});
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [roleDetails, setRoleDetails] = useState<ILooseObject>({});
  const [defaultRoleValues, setDefaultRolesValues] = useState<ILooseObject>({});
  const [inputOptions, setInputOptions] = useState<ILooseObject>({});
  const [roleUiMode, setRoleUiMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const [userRoleTableDetails, setUserRoleTableDetails] = useState<ListingTableDataStateType>({
    rows: [],
    columns: []
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const roleKey = searchParams.get('roleKey');
  const applicationKey = searchParams.get('applicationKey');

  const userAppPermissions: ILooseObject = useSelector(
    (state: RootState) => state?.userDetails?.OptionsAndPermissions?.appPermissions
  );

  const getAppPermissions = () => {
    //filtering permissions who have users applications
    return Object.entries(userAppPermissions as Record<string, string[]>)?.reduce(
      (acc: string[], [key, permissions]: [string, string[]]) => {
        if (key === applicationKey) {
          return Array.from(new Set([...acc, ...permissions]));
        }
        return acc;
      },
      []
    );
  };

  const processDefaultRoleData = (data: ILooseObject) => {
    const application = data?.application?.ui_label ?? '-';
    const permissions =
      data?.permissions?.map((permission: ILooseObject) => permission?.permission_key) ?? [];
    const role = data?.ui_label ?? '-';

    return { application, permissions, role };
  };

  const {
    control: controlRoleDetails,
    handleSubmit: handleSubmitRoleDetails,
    formState: {
      errors: errorsRoleDetails,
      isSubmitting: isSubmittingRoleDetails,
      isDirty: isDirtyRoleDetails
    },
    clearErrors: clearErrorsRoleDetails,
    reset: resetRoleDetails
  } = useForm({
    defaultValues: defaultRoleValues,
    // resolver: yupResolver(schema, { abortEarly: false }),
    mode: 'onBlur'
  });

  const getRoleDetails = async (roleKey: string) => {
    try {
      setIsLoading(true);
      const results = await AllRolesService.getRoleDetails({ roleKey });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        setRoleDetails({});
      } else if (!results.error.code) {
        setRoleDetails(results?.data);
        const processedRoleData = processDefaultRoleData(results?.data);
        setDefaultRolesValues(processedRoleData);
        const permissionsOptions: IAttributeOptions[] = results?.data?.permissions?.map(
          (permission: ILooseObject) => ({
            label: permission?.ui_label ?? '-',
            value: permission?.permission_key ?? '-',
            details: permission.description
          })
        );
        setInputOptions((prev: ILooseObject) => ({
          ...prev,
          permissions: permissionsOptions
        }));
        resetRoleDetails(processedRoleData);
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
      setRoleDetails({});
    } finally {
      setIsLoading(false);
    }
  };

  const getAllPermissions = async (applicationKey: string) => {
    try {
      setIsOptionsLoading((prev: ILooseObject) => ({ ...prev, permissions: true }));
      const results = await AllRolesService.getAllPermissions({ applicationKey });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        setRoleDetails({});
      } else if (!results.error.code) {
        // collecting old permissions, few permission are not the part of this role,
        // because few permissions have been attached as default for all applications
        let collectionExistingAndNewPermission: { [key: string]: IAttributeOptions } = (
          inputOptions?.permissions ?? []
        )?.reduce((acc: { [key: string]: IAttributeOptions }, permission: IAttributeOptions) => {
          acc[String(permission?.value)] = permission;
          return acc;
        }, {});
        const newPermissions = results?.data?.reduce(
          (acc: { [key: string]: IAttributeOptions }, permission: ILooseObject) => {
            const option: IAttributeOptions = {
              label: permission?.ui_label ?? '-',
              value: permission?.permission_key ?? '-',
              details: permission?.description
            };
            acc[String(permission?.permission_key)] = option;
            return acc;
          },
          {}
        );
        collectionExistingAndNewPermission = {
          ...collectionExistingAndNewPermission,
          ...newPermissions
        };

        setInputOptions((prev: ILooseObject) => ({
          ...prev,
          permissions: Object.values(collectionExistingAndNewPermission)
        }));
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsOptionsLoading((prev: ILooseObject) => ({ ...prev, permissions: false }));
    }
  };

  const getAllUsersByRoleKey = async (roleKey: string) => {
    try {
      setIsLoadingUsers(true);
      const results = await AllRolesService.getAllUsersByRole({ roleKey });
      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        setRoleDetails({});
      } else if (!results.error.code) {
        setUserRoleTableDetails({ rows: results?.rows ?? [], columns: results?.columns ?? [] });
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const addPermissionToRole = async (
    data: ILooseObject,
    filters: ILooseObject
  ): Promise<IApiClientResponse | null> => {
    try {
      const results = await AllRolesService.addPermissionsInRoleByApplication({ data, filters });
      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        enqueueSnackbar(AppMessages.Success.permission_added_in_roles, {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.success
        });
      }
      return results;
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
    return null;
  };

  const removePermissionToRole = async (
    data: ILooseObject,
    filters: ILooseObject
  ): Promise<IApiClientResponse | null> => {
    try {
      const results = await AllRolesService.removePermissionsInRoleByApplication({ data, filters });
      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        enqueueSnackbar(AppMessages.Success.permission_removed_in_roles, {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.success
        });
      }
      return results;
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
    return null;
  };

  const handleUpdateRoleDetails = async (data: ILooseObject) => {
    const { permissions } = data;
    const version = roleDetails?.version ?? 0;

    const removedPermissions = defaultRoleValues.permissions.filter(
      (permission: string) => !permissions.includes(permission)
    );

    const addedPermissions = permissions.filter(
      (permission: string) => !defaultRoleValues.permissions.includes(permission)
    );
    let updatedVersion = version;
    const updatePermissions = async () => {
      let isUpdated = false;
      if (removedPermissions.length > 0) {
        const results = await removePermissionToRole(
          { roleKey, applicationKey },
          { permission_keys: removedPermissions, v: updatedVersion }
        );
        if (results && !results.error.code) {
          isUpdated = true;
          updatedVersion = results?.data?.version ?? 0;
        }
      }

      if (addedPermissions.length > 0) {
        const results = await addPermissionToRole(
          { roleKey, applicationKey },
          { permission_keys: addedPermissions, v: updatedVersion }
        );
        if (results && !results.error.code) {
          isUpdated = true;
        }
      }

      if (isUpdated) {
        getRoleDetails(roleKey as string);
        setRoleUiMode('VIEW');
      }
    };
    updatePermissions();
  };

  const handleEditRoleDetails = () => {
    setRoleUiMode('EDIT');
    if (applicationKey) {
      getAllPermissions(applicationKey);
    }
  };

  const handleResetRoleDetails = () => {
    setRoleUiMode('VIEW');
    resetRoleDetails(defaultRoleValues);
  };

  useEffect(() => {
    if (roleKey) {
      if (getAppPermissions()?.includes('role_view')) {
        getRoleDetails(roleKey);
      }
      if (getAppPermissions()?.includes('role_users_view')) {
        getAllUsersByRoleKey(roleKey);
      }
    } else {
      enqueueSnackbar('Role key is missing', {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey]);

  if (
    !getAppPermissions()?.includes('role_view') ||
    !getAppPermissions()?.includes('role_users_view')
  ) {
    return <NoAuthorization />;
  }

  return (
    <Box>
      <DynamicBreadCrumbs
        breadCrumbsDetails={[
          { key: 1, name: 'All roles', redirectPath: '/roles' },
          { key: 2, name: 'Role Details' }
        ]}
      />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmitRoleDetails(handleUpdateRoleDetails)}
          display="flex"
          flexDirection="column"
          rowGap={2}
          mt={2}
        >
          <Box
            border={1}
            borderColor={lightTheme.palette.grey[300]}
            borderRadius={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              textTransform="none"
              color="primary.dark"
              pb={2}
              textAlign="center"
            >
              Role details
            </Typography>
            {getAppPermissions()?.includes('role_view') ? (
              <>
                <Grid2 container columnSpacing={10}>
                  {AttributesConfig.ROLE_DETAILS.map((attribute: IAttributeDef) => {
                    const inputType = UI_TYPE_INPUT_TYPE_MAP[attribute?.ui_type?.toUpperCase()];
                    const options = attribute?.options ?? inputOptions?.[attribute?.name] ?? [];
                    const inputFieldType: INPUT_TYPES_ENUM =
                      INPUT_FIELD_TYPE_MAP[attribute?.value_type?.toUpperCase()];
                    const isDisabled = !attribute?.is_editable || roleUiMode === 'VIEW';
                    const isVisible: boolean = !!attribute?.is_visible;
                    if (!isVisible) {
                      return null;
                    }

                    return (
                      <Grid2
                        container
                        size={{ xs: attribute?.name === 'permissions' ? 12 : 6 }}
                        key={attribute?.attribute_id}
                        alignItems="center"
                        display="block"
                      >
                        {/* UI label */}
                        <Box display="flex" pb={1}>
                          <Typography>{`${attribute?.ui_label} ${attribute?.is_mandatory ? '*' : ''}`}</Typography>
                        </Box>
                        {/* DynamicInput */}
                        {isOptionsLoading?.[attribute?.name] ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={30} />
                          </Box>
                        ) : (
                          <Controller
                            name={attribute.name as string}
                            control={controlRoleDetails}
                            render={({ field: { onChange, onBlur, value, name } }) => (
                              <DynamicInput
                                type={inputType}
                                id={attribute?.attribute_id}
                                label={attribute?.ui_label}
                                value={value}
                                required={attribute?.is_mandatory}
                                hasError={!!(errorsRoleDetails?.[name]?.message as string)}
                                helperText={(errorsRoleDetails?.[name]?.message as string) ?? ' '}
                                options={options}
                                maxLength={attribute?.max_length}
                                disabled={isDisabled}
                                placeholder={attribute?.placeholder}
                                disableClearable={attribute?.disable_clearable}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                  onChange(e);
                                  clearErrorsRoleDetails(name);
                                }}
                                onBlur={onBlur}
                                inputType={inputFieldType}
                              />
                            )}
                          />
                        )}
                      </Grid2>
                    );
                  })}
                </Grid2>

                <Box display="flex" alignSelf="flex-end" mt={2}>
                  {getAppPermissions()?.includes('application_role_add_permission') &&
                    getAppPermissions()?.includes('application_role_delete_permission') &&
                    (roleUiMode === 'VIEW' ? (
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        onClick={handleEditRoleDetails}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit role
                      </LoadingButton>
                    ) : (
                      <Box display="flex" columnGap={2}>
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          onClick={handleResetRoleDetails}
                        >
                          Cancel
                        </LoadingButton>
                        <LoadingButton
                          size="small"
                          variant="contained"
                          type="submit"
                          loading={isSubmittingRoleDetails}
                          disabled={
                            Object.keys(errorsRoleDetails).length > 0 || !isDirtyRoleDetails
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          Update
                        </LoadingButton>
                      </Box>
                    ))}
                </Box>
              </>
            ) : (
              <NoAuthorization />
            )}
          </Box>
          <Divider />
          <Box
            border={1}
            borderColor={lightTheme.palette.grey[300]}
            borderRadius={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              textTransform="none"
              color="primary.dark"
              pb={2}
              textAlign="center"
            >
              Users with above roles
            </Typography>
            {getAppPermissions()?.includes('role_users_view') ? (
              <DataGrid
                getRowId={(row) => row?.user_key}
                rows={userRoleTableDetails.rows}
                columns={userRoleTableDetails.columns}
                disableRowSelectionOnClick
                disableColumnSelector
                disableColumnMenu
                disableDensitySelector
                getRowHeight={() => 'auto'}
                sx={{
                  '& .MuiDataGrid-columnHeaderTitle': {
                    textOverflow: 'clip',
                    whiteSpace: 'break-spaces',
                    lineHeight: 1.15,
                    fontWeight: 600
                  },
                  '& .MuiDataGrid-row': {
                    minHeight: '52px !important'
                  },
                  '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
                    outline: 'none'
                  },
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none'
                  },
                  '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none'
                  },
                  '& .MuiDataGrid-main': {
                    overflow: 'unset'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    position: 'sticky',
                    top: 63,
                    zIndex: 1
                  },
                  '& .MuiDataGrid-container--top': {
                    zIndex: 1
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: lightTheme.palette.grey[100]
                  }
                }}
                slotProps={{
                  pagination: {
                    ActionsComponent: CustomTablePaginationActions
                  },
                  toolbar: {
                    csvOptions: { disableToolbarButton: true },
                    printOptions: { disableToolbarButton: true },
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 }
                  }
                }}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 20, page: 0 }
                  }
                }}
                paginationMode="client"
                loading={isLoadingUsers}
                pageSizeOptions={[20, 50, 100]}
              />
            ) : (
              <NoAuthorization />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RoleDetails;
